/**
 * A page's declaration of how it wants the shell to behave.
 *
 * Rendered as an inert, display:none marker whose data attributes AppShell reads
 * with Tailwind `has-*` variants. A CSS-only opt-in rather than a prop or a
 * context: the layout renders one shell for every route and never knows which
 * page is mounted, and `:has()` matches a display:none element just fine.
 *
 * Usage:
 *   <PageChrome layout="bleed" />   full viewport under the header
 *   <PageChrome width="full" />     no content column; page scrolls
 *   <PageChrome width="wide" />     6xl column
 *
 * There used to be a `separated` flag here for a page that scrolls in its own
 * panes. All it did was force the header's shadow, since such a page never moves
 * the shell's scrollport and so never triggered it. The shadow is gone — the
 * chrome fade separates the header from what passes under it — and with it the
 * only thing that flag controlled.
 */
export function PageChrome({
  layout,
  width,
}: {
  /** `bleed` — the page owns the full viewport under the header and its own
   *  scrolling (the research canvas). */
  layout?: "bleed"
  /** `wide` — 3xl → 6xl. `full` — no column at all, for a page that draws its own
   *  full-width sections (marketing, pricing) or its own measured column (the reader) but
   *  still scrolls normally. */
  width?: "wide" | "full"
}) {
  return (
    <div
      hidden
      data-page-layout={layout}
      data-page-width={width}
    />
  )
}
